import {
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react'
import { useSidebarStore } from '../../stores/sidebarStore'

/**
 * Standardised sidebar expand/collapse toggle button.
 *
 * Shared across SuperAdminLayout, AdminLayout, and UserLayout so the
 * affordance is identical wherever it appears. Reads + writes the
 * shared `sidebarStore`, so flipping it in one panel persists across
 * every other panel the same user visits.
 *
 * Two layouts:
 *   • `placement="header"` (default) — small 28px square button that
 *     sits inside the sidebar header next to the logo. Used to be the
 *     only placement.
 *   • `placement="edge"` — circular floating button that sits on the
 *     RIGHT EDGE of the sidebar (overlapping the boundary between
 *     sidebar and main content). The parent `<aside>` must be
 *     `relative` for the absolute positioning to land correctly.
 *
 * The icon flips between PanelLeftClose (expanded → about to close)
 * and PanelLeftOpen (collapsed → about to open). The native `title`
 * + `aria-label` carry the action both visually and to screen readers.
 */
export function SidebarToggle({
  tone = 'dark',
  placement = 'header',
  className = '',
}) {
  const collapsed = useSidebarStore((s) => s.collapsed)
  const toggle = useSidebarStore((s) => s.toggle)

  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar'

  if (placement === 'edge') {
    // Floating SOLID-FILL circle that straddles the sidebar's right
    // edge. Earlier iterations used a white-card background with an
    // accent ring + accent-coloured chevron, which the user reported
    // as not visible — the white-on-light contrast washed out the
    // icon against both the sidebar header AND the page canvas.
    //
    // Switched to a primary-blue solid fill with a WHITE chevron so
    // the button reads as an obvious clickable affordance regardless
    // of the surface behind it. Also swapped the icon from
    // `PanelLeftClose` (panel-with-tiny-chevron — chevron is ~5 px
    // at this size) to `ChevronLeft` / `ChevronRight` so the
    // direction signal is the full glyph at 18 px.
    const EdgeIcon = collapsed ? ChevronRight : ChevronLeft
    return (
      <button
        type="button"
        onClick={toggle}
        title={label}
        aria-label={label}
        aria-pressed={collapsed}
        className={
          // Vertical anchor: in the wide sidebar the toggle sits up in the
          // logo band (38px). In the COLLAPSED 68px rail the logo is centred
          // and the edge toggle would crowd right up against it, so drop the
          // toggle onto the header divider (84px header height) to keep clear
          // spacing between the logo and the toggle.
          `absolute ${collapsed ? 'top-[84px]' : 'top-[38px]'} right-0 translate-x-1/2 -translate-y-1/2 z-20 ` +
          // Desktop-only: the collapse-to-rail toggle makes no sense for the
          // mobile off-canvas drawer (opened via the header hamburger), and
          // on a slid-off drawer it would otherwise poke out at the screen's
          // left edge. Hidden below lg; unchanged on desktop.
          'hidden lg:inline-flex h-8 w-8 items-center justify-center rounded-full ' +
          'bg-[color:var(--color-primary)] text-white shadow-lg ring-2 ring-white ' +
          'hover:bg-[color:var(--color-primary-hover)] ' +
          // Pin the dark-mode fill to the SAME brand navy as light — the
          // `--color-primary` token otherwise brightens to blue in dark, so
          // without this the edge toggle looks different across themes.
          'dark:bg-[#0a2349] dark:hover:bg-[#0d2c5c] ' +
          'transition-colors cursor-pointer ' +
          className
        }
      >
        <EdgeIcon size={18} strokeWidth={2.5} />
      </button>
    )
  }

  // Default header placement — original 28px square button.
  const toneClass =
    tone === 'dark'
      ? 'text-[color:var(--color-ink-muted)] hover:text-white hover:bg-[color:var(--color-card-hover)]'
      : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]'

  return (
    <button
      type="button"
      onClick={toggle}
      title={label}
      aria-label={label}
      aria-pressed={collapsed}
      className={
        'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors cursor-pointer shrink-0 ' +
        toneClass +
        ' ' +
        className
      }
    >
      <Icon size={16} strokeWidth={1.75} />
    </button>
  )
}
